<script setup>
// import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import SystemLoadingPanel from './components/SystemLoadingPanel'
import ErrorToasts from './error-toast/ErrorToasts'
import UserMessageToast from './components/UserMessageToast'
import NewVersionAvailablePanel from './components/NewVersionAvailablePanel'

/* eslint-disable max-len */
</script>

<template>
  <!--<VueQueryDevtools />-->
  <RouterView />
  <SystemLoadingPanel />
  <ErrorToasts />
  <UserMessageToast />
  <NewVersionAvailablePanel />
</template>

<style>
html {
  font-size: 0.75rem;
}

@media print {
  html {
    font-size: 16px;
  }

  #app {
    padding: 1cm 1cm 0;
  }

  /* hide vertexes in print */
  g[data-type="vertices"] g[data-type="main"] circle[data-type="circle"] {
    display: none !important;
  }

  /* Every name at full strength on paper. On screen a name is faded so that it keeps out of the way of the
     drawing until it is hovered or picked out; on paper there is nothing to hover and nothing to pick out, so
     the fade buys nothing and costs legibility. A printer has no way to render a faded letter but to halftone
     it, which at the size a name is set breaks the letters up rather than lightening them. The opacity a label
     carries is an attribute rather than a style, so this overrides it without having to be forced. */
  g[data-type="labels"] text {
    opacity: 1;
  }
}

/* A condition the user should know of, in the amber of the app's other states (OLD VERSION, PAUSED): a tint of
   amber, opaque over the drawing */
.mtr-notice-warning {
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background: linear-gradient(rgba(var(--bs-warning-rgb), 0.2), rgba(var(--bs-warning-rgb), 0.2)), var(--bs-body-bg);
  color: var(--bs-warning-text-emphasis);
}

/* A field left empty that needs a value, in the amber of the app's other warnings */
.form-control.mtr-missing {
  border-color: var(--bs-warning);
}

.form-control.mtr-missing:focus {
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-warning-rgb), 0.25);
}

.mtr-editor-right-panel {
  position: absolute;
  right: 10px;
  opacity: 0.95;
}

.mtr-layout-left-panel {
  position: absolute;
  left: 10px;
  opacity: 0.95;
  overflow-y: auto;
}

.mtr-btn-sm-icon {
  padding: 3px 10px 2px;
}

.mtr-lh-inherit {
  line-height: inherit !important;
}

.mtr-on-top {
  z-index: 9;
}

.mtr-form-name {
  font-weight: normal;
  line-height: inherit
}

.lh-sm .form-label {
  margin-bottom: 0.25rem;
}

svg text {
  font-family: inherit;
}

.mtr-text-overflow-ellipsis {
  text-overflow: ellipsis;
}

.mtr-text-decoration-line-none {
  text-decoration-line: none;
}

.mtr-keep-with-next {
  break-after: avoid-page;
}

.mtr-avoid-page-break {
  break-inside: avoid-page;
}

.mtr-cursor-pointer {
  cursor: pointer;
}

/* A Material Symbols icon (AppIcon): sized and coloured by the text around it, like an icon font */
.mtr-icon {
  display: inline-block;
  width: 1.25em;
  height: 1.25em;
  vertical-align: -0.25em;
}

.mtr-icon svg {
  display: block;
  width: 100%;
  height: 100%;
  fill: currentcolor;
}

.mtr-card-title {
  font-size: 1.1667rem;
  font-weight: 450;
}

/* Cards fill the width: as many 16rem columns as fit, sharing what is left over, so a column is added or
   dropped as the window resizes and the cards stretch slightly in between */
.mtr-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: 1rem;
}

/* Deliberately narrower than two cards, so it doesn't pretend to be part of the grid */
.mtr-search-col {
  max-width: calc(30rem + var(--bs-gutter-x));
}

/* A button that sits in a row of form fields and matches their height, background and border */
.mtr-toolbar-button {
  --bs-btn-color: var(--bs-body-color);
  --bs-btn-bg: var(--bs-body-bg);
  --bs-btn-border-color: var(--bs-border-color);
  --bs-btn-hover-color: var(--bs-body-color);
  --bs-btn-hover-bg: var(--bs-tertiary-bg);
  --bs-btn-hover-border-color: var(--bs-border-color);
  --bs-btn-active-color: var(--bs-primary-text-emphasis);
  --bs-btn-active-bg: var(--bs-primary-bg-subtle);
  --bs-btn-active-border-color: var(--bs-primary-border-subtle);
}

/* Borderless tool button (editor toolbar), after Google Docs: nothing but the icon at rest, a light grey fill on hover,
   a light navy fill with a navy icon while on (.active), and a navy ring when kept on for repeated use (.locked) */
.btn-tool {
  --bs-btn-padding-x: 0.5rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-color: var(--bs-body-color);
  --bs-btn-bg: transparent;
  --bs-btn-border-color: transparent;
  --bs-btn-hover-color: var(--bs-body-color);
  --bs-btn-hover-bg: rgba(var(--bs-body-color-rgb), 0.08);
  --bs-btn-hover-border-color: transparent;
  --bs-btn-active-color: var(--bs-primary);
  --bs-btn-active-bg: var(--bs-primary-bg-subtle);
  --bs-btn-active-border-color: transparent;
  --bs-btn-disabled-color: var(--bs-body-color);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-btn-disabled-opacity: 0.35;
  --bs-btn-focus-shadow-rgb: var(--bs-primary-rgb);
  --bs-btn-border-radius: 0.3333rem;
}

.btn-tool.locked {
  box-shadow: inset 0 0 0 1px var(--bs-primary);
}

/* A group of tool buttons, set off from the one before by a thin divider instead of a border round each button */
.mtr-tool-group {
  display: inline-flex;
  gap: 0.125rem;
  align-items: center;
  vertical-align: top;
  margin-left: 0.25rem;
}

.mtr-tool-group::before {
  content: "";
  width: 1px;
  height: 1.5rem;
  margin-right: 0.25rem;
  background-color: var(--bs-border-color);
}

.mtr-tool-group:first-child {
  margin-left: 0;
}

.mtr-tool-group:first-child::before {
  display: none;
}

/* A switch between options (editor modes, 2D/3D): tool buttons side by side, the chosen one shown as on */
.mtr-segmented {
  display: inline-flex;
  gap: 0.125rem;
}

/* A tool that is on stays looking on where it is disabled because choosing it again would do nothing */
.btn-tool.active {
  --bs-btn-disabled-color: var(--bs-btn-active-color);
  --bs-btn-disabled-bg: var(--bs-btn-active-bg);
  --bs-btn-disabled-opacity: 1;
}

/* Tool buttons that show their on state in a colour of their own (pause, play). Amber and lime are light, so the
   icon takes the dark shade of the colour and the fill a stronger tint, for the contrast navy has on its own. */
.btn-tool-warning {
  --bs-btn-active-color: var(--bs-warning-text-emphasis);
  --bs-btn-active-bg: rgba(var(--bs-warning-rgb), 0.35);
}

.btn-tool-success {
  --bs-btn-active-color: var(--bs-success-text-emphasis);
  --bs-btn-active-bg: rgba(var(--bs-success-rgb), 0.35);
}

/* Round, borderless menu button at the right end of a card title, pulled into the corner so the title row keeps its height */
.mtr-card-menu-button {
  --bs-btn-color: var(--bs-body-color);
  --bs-btn-hover-color: var(--bs-body-color);
  --bs-btn-hover-bg: rgba(var(--bs-body-color-rgb), 0.08);
  --bs-btn-active-color: var(--bs-body-color);
  --bs-btn-active-bg: rgba(var(--bs-body-color-rgb), 0.12);
  --bs-btn-hover-border-color: transparent;
  --bs-btn-active-border-color: transparent;

  width: 2.25rem;
  height: 2.25rem;
  margin: -0.5rem -0.75rem -0.5rem 0;
  padding: 0;
  border-radius: 50%;
  font-size: 1.333rem;
  line-height: 1;
}

p:last-child {
  margin-bottom: 0;
}

.alert {
  margin-top: 1rem;
  margin-bottom: 0;
}

tr.text-muted td,
tr.text-muted th {
  color: inherit;
}

.mtr-docs-link-card {
  width: 14rem;
  min-height: 8rem;
}

.mtr-docs-image-link-card {
  width: 20rem;
  min-height: 22rem;
}

.mtr-docs-link-card:hover, a:focus .mtr-docs-link-card,
.mtr-docs-image-link-card:hover, a:focus .mtr-docs-image-link-card {
  background-color: #f8f8fa;
}

.mtr-docs-img {
  object-fit: cover;
}

.mtr-docs-do {
  border-top-width: 1rem !important;
  margin-bottom: -0.25rem;
}

div.mtr-docs-video-cover.paused::before {
  content: "";
  position: absolute;
  background-color: #00000080;
  inset: 0;
  background-image: url('data:image/svg+xml;utf-8, <svg xmlns= "http://www.w3.org/2000/svg" enable-background= "new 0 0 24 24" viewBox= "0 0 24 24" fill= "white" width= "48px" height= "48px" > <g> <rect fill= "none" height= "24" width= "24" /></g><g><path d= "M12,2C6.48,2,2,6.48,2,12s4.48,10,10,10s10-4.48,10-10S17.52,2,12,2z M9.5,16.5v-9l7,4.5L9.5,16.5z" /> </g> </svg>');
  background-repeat: no-repeat;
  background-position-x: 50%;
  background-position-y: 50%;
  background-size: 72px;
}

div.mtr-docs-video-cover.playing:hover::before {
  content: "";
  position: absolute;
  background-color: #00000040;
  inset: 0;
  background-image: url('data:image/svg+xml;utf-8,<svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" viewBox="0 0 24 24" fill="white" width="48px" height="48px"><g><rect fill="none" height="24" width="24"/></g><g><g><path d="M12,2C6.48,2,2,6.48,2,12s4.48,10,10,10s10-4.48,10-10S17.52,2,12,2z M11,16H9V8h2V16z M15,16h-2V8h2V16z"/></g></g></svg>');
  background-repeat: no-repeat;
  background-position-x: 50%;
  background-position-y: 50%;
  background-size: 72px;
}

.table-sticky-head thead {
  position: sticky;
  top: 3.4rem;
}

[data-bs-theme="dark"] {
  --bs-body-bg: var(--bs-black);
}

/* Customize Bootstrap 5.3 range slider track color to darker gray as of v. 5.2 */
.form-range::-webkit-slider-runnable-track {
  background-color: #dee2e6;
}

/* Animated list */
.animated-list-enter-active, .animated-list-leave-active {
  transition: all 0.5s ease;
}

.animated-list-enter-from {
  opacity: 0;
  transform: translateY(5rem);
}

.animated-list-leave-to {
  opacity: 0;
  transform: translateX(20%) scaleX(60%);
}

/* Bootstrap customization start */

body {
  background-color: var(--bs-light);
}

.card {
  --bs-card-spacer-y: 1rem;
  --bs-card-spacer-x: 1rem;
  --bs-card-title-spacer-y: 0.5rem;
  --bs-card-title-color: ;
  --bs-card-subtitle-color: ;
  --bs-card-border-width: var(--bs-border-width);
  --bs-card-border-color: var(--bs-border-color-translucent);
  --bs-card-border-radius: var(--bs-border-radius);
  --bs-card-box-shadow: ;
  --bs-card-inner-border-radius: calc(var(--bs-border-radius) - (var(--bs-border-width)));
  --bs-card-cap-padding-y: 1rem;
  --bs-card-cap-padding-x: 1rem;
  --bs-card-cap-bg: rgba(var(--bs-body-color-rgb), 0.03);
  --bs-card-cap-color: var(--bs-primary);
  --bs-card-height: ;
  --bs-card-color: ;
  --bs-card-bg: var(--bs-body-bg);
  --bs-card-img-overlay-padding: 1rem;
  --bs-card-group-margin: 0.75rem;
}

.modal-header {
  background-color: rgba(var(--bs-body-color-rgb), 0.03);
  color: var(--bs-primary);
}

/* Custom btns */

.btn-mth-primary {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-primary);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-primary-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-primary-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-primary-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-primary);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

.btn-mth-secondary {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-secondary);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-secondary-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-secondary-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-secondary-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-secondary);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

.btn-mth-success {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-success);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-success-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-success-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-success-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-success);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

.btn-mth-info {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-info);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-info-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-info-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-info-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-info);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

.btn-mth-warning {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-warning);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-warning-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-warning-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-warning-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-warning);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

.btn-mth-danger {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-danger);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-danger-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-danger-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-danger-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-danger);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

.btn-mth-light {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-light);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-light-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-light-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-light-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-light);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

.btn-mth-dark {
  --bs-btn-padding-x: 0.25rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-line-height: 1;
  --bs-btn-color: var(--bs-dark);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: rgba(var(--bs-dark-rgb), 0.4);
  --bs-btn-focus-shadow-rgb: var(--bs-dark-rgb);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: rgba(var(--bs-dark-rgb), 0.6);
  --bs-btn-active-shadow: inset 0 3px 5px rgba(0 0 0 / 12.5%);
  --bs-btn-disabled-color: var(--bs-dark);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: transparent;
  --bs-gradient: none;
}

/* Bootstrap customization end */

.mtr-pointer-events-remove {
  pointer-events: none;
}

.mtr-pointer-events-remove button,
.mtr-pointer-events-remove input,
.mtr-pointer-events-remove a {
  pointer-events: all;
}
</style>
